import React, { useState, useEffect, useRef } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_ANON_KEY } from '../lib/supabaseClient';
import { useAuth } from '../contexts/auth/AuthContext';
import StripeProvider from '../components/stripe/StripeProvider';
import { edgeFunctionUrl, EdgeFunctions } from '../lib/edgeFunctions';
import MembershipAgreementForm, { MembershipAgreementData } from '../components/forms/MembershipAgreementForm';

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true,
};

// Step indicator component
const StepIndicator = ({ currentStep }: { currentStep: number }) => {
  const steps = [
    { num: 1, label: 'Agreement' },
    { num: 2, label: 'Payment' },
  ];

  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={step.num}>
          <div className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                currentStep >= step.num
                  ? 'bg-[#3A5E63] text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {currentStep > step.num ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step.num
              )}
            </div>
            <span
              className={`ml-2 text-sm font-medium ${
                currentStep >= step.num ? 'text-[#3A5E63]' : 'text-gray-500'
              }`}
            >
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-16 h-1 mx-4 rounded ${
                currentStep > step.num ? 'bg-[#3A5E63]' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Payment form component (step 2)
const PaymentForm = ({
  onBack,
}: {
  onBack: () => void;
}) => {
  const { user, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const creationInitiatedRef = useRef(false);

  useEffect(() => {
    const createSubscription = async () => {
      if (!user || creationInitiatedRef.current) {
        if (!user) {
          setErrorMessage('You must be logged in to subscribe.');
        }
        return;
      }

      creationInitiatedRef.current = true;
      setIsProcessing(true);
      setErrorMessage(null);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        if (!accessToken) {
          throw new Error('Authentication required.');
        }

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        };

        const response = await fetch(
          edgeFunctionUrl(EdgeFunctions.CREATE_SUBSCRIPTION),
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              email: user.email,
              name: user.user_metadata.full_name || 'New Member',
              user_id: user.id,
            }),
          }
        );

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.error || 'Failed to create subscription.');
        }

        if (!responseData.clientSecret) {
          throw new Error('No client secret returned from server.');
        }

        setClientSecret(responseData.clientSecret);
      } catch (error: any) {
        console.error('Error creating subscription:', error);
        setErrorMessage(error.message || 'An unexpected error occurred.');
      } finally {
        setIsProcessing(false);
      }
    };

    createSubscription();
  }, [user]);

  const waitForMembershipActivation = async (): Promise<boolean> => {
    const maxAttempts = 20;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await refreshUserProfile();

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('membership_status')
        .eq('id', user!.id)
        .single();

      if (profile?.membership_status === 'active') {
        console.log(`Membership activated after ${attempt * 500}ms`);
        return true;
      }

      await new Promise(resolve => setTimeout(resolve, 500));
    }

    return false;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret || !user) {
      setErrorMessage('Stripe is not ready, or you are not logged in.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found.');

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: user.user_metadata.full_name || 'New Member',
            email: user.email,
          },
        },
      });

      if (error) {
        throw new Error(error.message || 'Payment failed.');
      }

      if (paymentIntent?.status === 'succeeded') {
        console.log('Subscription payment successful!');

        console.log('Waiting for membership activation...');
        const activated = await waitForMembershipActivation();

        if (activated) {
          console.log('Membership activated successfully!');
        } else {
          console.warn('Membership activation polling timed out, but payment succeeded.');
        }

        navigate('/join-success');
      } else {
        throw new Error(`Payment processing. Status: ${paymentIntent?.status}`);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setErrorMessage(err.message || 'An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-8 bg-white shadow-lg rounded-lg">
      <div className="bg-[#F1F7F4] p-6 rounded-md mb-8 text-center">
        <h3 className="text-xl font-semibold text-[#232347]">Monthly Membership</h3>
        <p className="text-4xl font-bold text-[#3A5E63] my-2">$209<span className="text-lg font-normal">/month</span></p>
        <p className="text-gray-600">Enjoy exclusive benefits and 20% off all services.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-lg text-[#232347] mb-2">Payment Details</label>
          <div className="border border-gray-300 p-4 rounded-md bg-white">
            <CardElement options={cardElementOptions} />
          </div>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md">
            {errorMessage}
          </div>
        )}

        {isProcessing && !clientSecret && (
          <div className="flex items-center justify-center text-gray-600">
            <span className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full mr-3"></span>
            Preparing your subscription...
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            className="px-6 py-3 rounded-md border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!stripe || !elements || isProcessing || !clientSecret}
            className="flex-1 px-6 py-3 rounded-md text-white font-semibold bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isProcessing ? (
              <>
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-3"></span>
                Processing...
              </>
            ) : (
              'Subscribe Now'
            )}
          </button>
        </div>

        <p className="text-sm text-gray-500 text-center mt-4">
          Your membership will automatically renew monthly. You can cancel anytime from your profile.
        </p>
      </form>
    </div>
  );
};

// Main component that manages both steps
const MembershipCheckoutFlow = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmittingAgreement, setIsSubmittingAgreement] = useState(false);
  const [agreementError, setAgreementError] = useState<string | null>(null);
  const [hasExistingAgreement, setHasExistingAgreement] = useState(false);
  const [isCheckingAgreement, setIsCheckingAgreement] = useState(true);

  // Check if user already has a signed agreement
  useEffect(() => {
    const checkExistingAgreement = async () => {
      if (!user) {
        setIsCheckingAgreement(false);
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('membership_agreement_id')
          .eq('id', user.id)
          .single();

        if (profile?.membership_agreement_id) {
          setHasExistingAgreement(true);
          setCurrentStep(2); // Skip to payment
        }
      } catch (error) {
        console.error('Error checking agreement:', error);
      } finally {
        setIsCheckingAgreement(false);
      }
    };

    checkExistingAgreement();
  }, [user]);

  // Handle agreement submission
  const handleAgreementSubmit = async (data: MembershipAgreementData) => {
    setIsSubmittingAgreement(true);
    setAgreementError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('Authentication required.');
      }

      const response = await fetch(
        edgeFunctionUrl(EdgeFunctions.SUBMIT_MEMBERSHIP_AGREEMENT),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ agreementData: data }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit agreement');
      }

      // Move to payment step
      setCurrentStep(2);
    } catch (error: any) {
      console.error('Agreement submission error:', error);
      setAgreementError(error.message || 'Failed to submit agreement. Please try again.');
    } finally {
      setIsSubmittingAgreement(false);
    }
  };

  // Loading state
  if (isCheckingAgreement) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3A5E63] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Login Required</h2>
          <p className="text-gray-600 mb-6">Please log in or create an account to join our membership.</p>
          <button
            onClick={() => navigate('/login', { state: { from: '/join' } })}
            className="px-6 py-2 bg-[#3A5E63] text-white rounded-lg hover:bg-[#2d4a4d] transition-colors"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl font-serif text-center text-[#232347] mb-2">
          Join Our Membership
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Enjoy 20% off all services as a VIP member
        </p>

        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} />

        {/* Step 1: Agreement */}
        {currentStep === 1 && (
          <>
            {agreementError && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6 max-w-3xl mx-auto">
                {agreementError}
              </div>
            )}
            <MembershipAgreementForm
              memberName={userProfile?.full_name || user.user_metadata?.full_name || ''}
              monthlyFee={209}
              onSubmit={handleAgreementSubmit}
              onCancel={() => navigate(-1)}
              isSubmitting={isSubmittingAgreement}
            />
          </>
        )}

        {/* Step 2: Payment */}
        {currentStep === 2 && (
          <PaymentForm
            onBack={() => {
              // Only allow going back if they don't have an existing agreement
              if (!hasExistingAgreement) {
                setCurrentStep(1);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

// Main page component that wraps with StripeProvider
const JoinPage = () => {
  return (
    <StripeProvider>
      <MembershipCheckoutFlow />
    </StripeProvider>
  );
};

export default JoinPage;
